import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDemoSafeMutation } from "@/hooks/useDemoGuard";
import { tasksRepository } from "@/repositories";
import { useAuth } from "@/hooks/useAuth";
import type { Json } from "@/integrations/supabase/types";

export type TaskActivityKind =
  | "assignment_change"
  | "note"
  | "status_change"
  | "feedback"
  | "reassignment";

/** Anything safely JSON-serializable. Callers can pass plain objects/arrays. */
export type TaskActivityPayload = { [key: string]: Json } | Record<string, never>;

export interface TaskActivityRow {
  id: string;
  task_id: string;
  actor_id: string | null;
  kind: TaskActivityKind;
  payload: Record<string, unknown>;
  created_at: string;
  actor?: { id: string; full_name: string | null; full_name_en: string | null } | null;
}

export function useTaskActivity(taskId: string | undefined) {
  const { companyId } = useAuth();

  return useQuery({
    queryKey: ["task_activity", taskId, companyId],
    queryFn: async () => {
      if (!taskId || !companyId) return [];
      // Multi-tenant isolation: the parent task's company_id must match the
      // caller's company (enforced inside the repository via the inner join).
      const data = await tasksRepository.listActivity(taskId, companyId);
      return data as unknown as TaskActivityRow[];
    },
    enabled: !!taskId && !!companyId,
  });
}

export interface LogTaskActivityInput {
  task_id: string;
  kind: TaskActivityKind;
  payload?: TaskActivityPayload;
}

export function useLogTaskActivity() {
  const queryClient = useQueryClient();

  return useDemoSafeMutation({
    // actor_id is NOT sent — the guarded Nest endpoint stamps it from the
    // authenticated session, so attribution can't be spoofed client-side.
    mutationFn: async ({ task_id, kind, payload = {} }: LogTaskActivityInput) => {
      return tasksRepository.insertActivity({
        task_id,
        kind,
        payload: payload as Json,
      });
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ["task_activity", vars.task_id] });
    },
  });
}
